import React from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  CCO_TYPE_DISPLAY_NAME,
  UNCATEGORIZED_KEY,
  getGroupedCCOs,
} from "~/lib/ccos";
import { CCOType } from "~/lib/schemas";

export const CCOList = () => {
  const {
    data: groupedCCOs,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["grouped-ccos"],
    queryFn: () => getGroupedCCOs(),
  });

  // Define the order in which to display types
  const typeOrder: CCOType[] = [CCOType.PAO, CCOType.CTO];

  if (isLoading) {
    return <p>Loading organizations...</p>;
  }

  if (error) {
    return <p>Error loading organizations: {error.message}</p>;
  }

  if (!groupedCCOs) {
    return <p className="text-gray-500">No coaching organizations found.</p>;
  }

  return (
    <div className="space-y-8">
      {typeOrder.map((type) => {
        const typeGroup = groupedCCOs[type];
        if (!typeGroup) {
          return null;
        } // Skip type if no CCOs exist for it

        // Generate a display name for the type
        const typeDisplayName =
          CCO_TYPE_DISPLAY_NAME[type as keyof typeof CCO_TYPE_DISPLAY_NAME] ??
          type;

        return (
          <div key={type}>
            <h2 className="mb-5 border-b border-gray-300 pb-2 text-2xl font-semibold">
              {typeDisplayName}
            </h2>
            {Object.entries(typeGroup).map(([subcategory, ccosInGroup]) => (
              <div key={subcategory} className="mb-6">
                {/* Only show subcategory title if it's not the special 'Uncategorized' key */}
                {subcategory !== UNCATEGORIZED_KEY && (
                  <h3 className="mb-3 text-lg font-medium text-gray-700">
                    {subcategory}
                  </h3>
                )}
                <ul
                  className={`list-disc space-y-2 ${subcategory !== UNCATEGORIZED_KEY ? "pl-5" : "pl-0"}`}
                >
                  {ccosInGroup.map((cco) => (
                    <li key={cco.id}>
                      <Link
                        to="/coaches/cco/$slug"
                        params={{ slug: cco.slug }}
                        className="text-brand-800 hover:text-brand-700 underline hover:no-underline"
                        aria-label={`View details for ${cco.name}`}
                      >
                        {cco.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        );
      })}
      {0 === Object.keys(groupedCCOs).length && (
        <p className="text-gray-500">No coaching organizations found.</p>
      )}
    </div>
  );
};
